/**
 * Create dashboard (screenshots/83; brief §2.13 Create): name (required), optional description, Start from (Blank, Copy my
 * Overview, the group's templates), Who can see it (Only you · <team> · <team> can edit), "Star it so it shows in your
 * sidebar" (on by default). Enter creates; Esc closes. After create the dashboard opens in Edit mode with the widget library
 * (`?edit=1`). The Worker builds the layout (createDashboard) and copies your saved Overview itself.
 * From the handoff starter's src/analytics/CreateDashboardDialog.tsx.
 */
import { useRef, useState } from "react";
import { ArrowRight, Banknote, CheckCircle2, Circle, Copy, MapPin, Pencil, Plus, Sparkles, Star, Users, Lock, X, type LucideIcon } from "lucide-react";
import { navigate } from "../app/router";
import { navStore } from "../nav/navStore";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { toastStore } from "../stores/toastStore";
import { TEMPLATES, usableBy, type TemplateId, type Viewer } from "./dashboards";
import { analyticsStore, useAnalytics } from "./analyticsStore";
import { Thumb } from "./Thumb";

type StartKey = "blank" | "copyOverview" | TemplateId;
type Who = "private" | "team-view" | "team-edit";
const TEMPLATE_ICON: Record<TemplateId, LucideIcon> = { moneyLobbying: Banknote, votesForecasts: Sparkles, wardPulse: MapPin };

export function CreateDashboardDialog({ viewer, orgName, onClose }: { viewer: Viewer; orgName: string | null; onClose: () => void }) {
  const overview = useAnalytics((s) => s.snapshot?.overviewLayout ?? []);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState<StartKey>("blank");
  const [who, setWho] = useState<Who>("team-view");
  const [star, setStar] = useState(true);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLFormElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  useFocusTrap(box, true, { onEscape: onClose });
  const team = orgName || "Your team";
  const templates = (Object.keys(TEMPLATES) as TemplateId[]).filter((k) => TEMPLATES[k].groups.includes(viewer.group));
  const picked = start !== "blank" && start !== "copyOverview" ? TEMPLATES[start] : null;
  const pickedCount = picked ? usableBy(picked.layout, viewer).length : 0;

  const submit = async () => {
    if (!title.trim()) { nameRef.current?.focus(); return; }
    setBusy(true);
    try {
      const d = await analyticsStore.getState().create({ title: title.trim(), description: description.trim() || undefined,
        start: start === "blank" || start === "copyOverview" ? { kind: start } : { kind: "template", id: start },
        visibility: who === "private" ? "private" : "team", teamAccess: who === "team-edit" ? "edit" : "view" });
      if (star) navStore.getState().toggleStar(d.id, d.title);
      onClose();
      navigate(`/dashboard/analytics/${d.id}?edit=1`);
    } catch (e) {
      setBusy(false);
      toastStore.getState().show({ message: (e as Error).message || "Something went wrong" });
    }
  };

  const tiles: { key: StartKey; label: string; Icon: LucideIcon; thumb: React.ReactNode }[] = [
    { key: "blank", label: "Blank", Icon: Plus, thumb: <span className="an-tile__blank"><Plus size={16} aria-hidden /></span> },
    { key: "copyOverview", label: "Copy my Overview", Icon: Copy, thumb: <Thumb layout={overview} col={11} row={6} gap={3} /> },
    ...templates.map((k) => ({ key: k as StartKey, label: TEMPLATES[k].title, Icon: TEMPLATE_ICON[k], thumb: <Thumb layout={usableBy(TEMPLATES[k].layout, viewer)} col={11} row={6} gap={3} /> })),
  ];
  const whos: { key: Who; title: string; sub: string; Icon: LucideIcon }[] = [
    { key: "private", title: "Only you", sub: "Private until you share it", Icon: Lock },
    { key: "team-view", title: team, sub: "Everyone on your team can view", Icon: Users },
    { key: "team-edit", title: `${team} can edit`, sub: "Admins and members can change widgets", Icon: Pencil },
  ];

  return (
    <div className="an-scrim" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form ref={box} className="an-modal an-create-dialog" role="dialog" aria-modal="true" aria-labelledby="an-create-title" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <header className="an-modal__head">
          <h2 id="an-create-title">Create dashboard</h2>
          <button type="button" className="an-icon-btn" aria-label="Close" onClick={onClose}><X size={18} aria-hidden /></button>
        </header>
        <div className="an-modal__body">
          <label className="an-field"><span>Name</span>
            <input ref={nameRef} autoFocus required value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} /></label>
          <label className="an-field"><span>Description · optional</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} /></label>
          <fieldset className="an-fieldset">
            <legend>Start from</legend>
            <div className="an-tiles" style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}>
              {tiles.map(({ key, label, Icon, thumb }) => (
                <label key={key} className={`an-tile${start === key ? " is-on" : ""}`}>
                  <input type="radio" name="start" className="an-sr" checked={start === key} onChange={() => setStart(key)} />
                  <span className="an-tile__thumb">{thumb}</span>
                  <span className="an-tile__label"><Icon size={15} aria-hidden />{label}</span>
                </label>
              ))}
            </div>
            {picked?.blurb && <p className="an-help">{picked.title}: {picked.blurb}. {pickedCount} widgets, all editable.</p>}
          </fieldset>
          <fieldset className="an-fieldset">
            <legend>Who can see it</legend>
            <div className="an-whos">
              {whos.map(({ key, title: t, sub, Icon }) => (
                <label key={key} className={`an-who${who === key ? " is-on" : ""}`}>
                  <input type="radio" name="who" className="an-sr" checked={who === key} onChange={() => setWho(key)} />
                  <Icon size={18} aria-hidden className="an-who__icon" />
                  <span className="an-who__text"><strong>{t}</strong><small>{sub}</small></span>
                  {who === key ? <CheckCircle2 size={16} aria-hidden className="an-who__radio" /> : <Circle size={16} aria-hidden className="an-who__radio" />}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="an-star-row">
            <button type="button" className="switch switch--md" role="switch" aria-checked={star} aria-labelledby="an-star-label" onClick={() => setStar(!star)} />
            <span id="an-star-label">Star it so it shows in your sidebar</span>
            <Star size={15} aria-hidden className="an-star-row__icon" />
          </div>
        </div>
        <footer className="an-modal__foot">
          <button type="button" className="btn btn--secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={!title.trim() || busy}><ArrowRight size={15} aria-hidden />Create dashboard</button>
        </footer>
      </form>
    </div>
  );
}
